// Dependency-free SVG line chart for daily funnel series. Built for the
// universe-widget Analytics tab; intentionally minimal — dense uniform
// series in (server zero-fills gaps), polylines out. If charting needs
// ever outgrow this (multiple chart types, zooming), revisit recharts.

import { useMemo, useState } from 'react';

const VIEW_W = 720;
const PAD = { top: 8, right: 8, bottom: 20, left: 40 };

// Round up to a "nice" axis max: 1/2/5 × 10^k.
function niceMax(v) {
  if (v <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(v)));
  for (const m of [1, 2, 5, 10]) {
    if (m * pow >= v) return m * pow;
  }
  return 10 * pow;
}

function fmtDay(day, long = false) {
  const s = String(day);
  // Hourly series label ('YYYY-MM-DDTHH:00', venue-local wall clock from the
  // server) — render the hour text as-is; parsing a zone-less string with
  // new Date() would reinterpret it in the browser's timezone.
  if (s.includes('T')) {
    const hh = s.slice(11);
    return long ? `${fmtDay(s.slice(0, 10))}, ${hh}` : hh;
  }
  // `day` is a UTC date string (YYYY-MM-DD) — must format in UTC or the
  // label can shift a day in western timezones.
  return new Date(s).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', timeZone: 'UTC',
  });
}

// series: [{ day, <key>: number, ... }] — dense, ascending by day.
// stages:  [{ key, label, className }] — className is a Tailwind text-color
//          class; strokes/fills use currentColor so the theme drives color.
export default function TrendChart({ series, stages, height = 180, defaultHidden = [] }) {
  const [hidden, setHidden] = useState(() => new Set(defaultHidden));
  const [hoverIdx, setHoverIdx] = useState(null);

  const visible = stages.filter((s) => !hidden.has(s.key));
  const n = series.length;
  // Clamp the hovered index — `series` can shrink (days change / per-listing
  // fetch) while a stale hoverIdx points past the new end, which would make
  // series[hoverIdx] undefined and crash the tooltip/markers below.
  const hover = hoverIdx == null ? null : Math.min(hoverIdx, n - 1);

  const { yMax, plotW, plotH, x, y } = useMemo(() => {
    const max = niceMax(Math.max(1, ...series.flatMap((row) =>
      visible.map((s) => row[s.key] || 0))));
    const pw = VIEW_W - PAD.left - PAD.right;
    const ph = height - PAD.top - PAD.bottom;
    return {
      yMax: max,
      plotW: pw,
      plotH: ph,
      x: (i) => PAD.left + (n > 1 ? (i / (n - 1)) * pw : pw / 2),
      y: (v) => PAD.top + ph - (v / max) * ph,
    };
  }, [series, visible, height, n]);

  if (!n) return null;

  const toggle = (key) => setHidden((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });

  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * VIEW_W;
    const i = Math.round(((px - PAD.left) / plotW) * (n - 1));
    setHoverIdx(Math.max(0, Math.min(n - 1, i)));
  };

  // X labels: at most ~8 across the width.
  const labelEvery = Math.max(1, Math.ceil(n / 8));

  return (
    <div className="relative">
      <div className="flex flex-wrap gap-1.5 mb-2">
        {stages.map((s) => (
          <button
            key={s.key}
            onClick={() => toggle(s.key)}
            className={`flex items-center gap-1.5 px-2 py-0.5 rounded-md border text-xs ${
              hidden.has(s.key)
                ? 'border-input text-muted-foreground/60'
                : 'border-input hover:bg-accent'
            }`}
          >
            <span
              className={`inline-block w-2.5 h-0.5 rounded-full bg-current ${s.className} ${hidden.has(s.key) ? 'opacity-30' : ''}`}
            />
            {s.label}
          </button>
        ))}
      </div>

      <svg
        viewBox={`0 0 ${VIEW_W} ${height}`}
        width="100%"
        className="block select-none"
        onMouseMove={onMove}
        onMouseLeave={() => setHoverIdx(null)}
      >
        {/* gridlines + y labels */}
        {[0, 0.25, 0.5, 0.75, 1].map((t) => {
          const gy = PAD.top + plotH - t * plotH;
          return (
            <g key={t}>
              <line
                x1={PAD.left} x2={VIEW_W - PAD.right} y1={gy} y2={gy}
                className="stroke-border" strokeWidth="1"
              />
              <text
                x={PAD.left - 6} y={gy + 3} textAnchor="end"
                className="text-[10px] fill-muted-foreground tabular-nums"
              >
                {Math.round(t * yMax).toLocaleString()}
              </text>
            </g>
          );
        })}

        {/* x labels, anchor edge labels inward so they don't clip */}
        {series.map((row, i) => (
          i % labelEvery === 0 && (
            <text
              key={row.day}
              x={x(i)} y={height - 5}
              textAnchor={x(i) > VIEW_W - 30 ? 'end' : x(i) < 30 ? 'start' : 'middle'}
              className="text-[10px] fill-muted-foreground"
            >
              {fmtDay(row.day)}
            </text>
          )
        ))}

        {/* hover guideline */}
        {hover != null && (
          <line
            x1={x(hover)} x2={x(hover)}
            y1={PAD.top} y2={PAD.top + plotH}
            className="stroke-muted-foreground/40" strokeWidth="1"
          />
        )}

        {/* series lines */}
        {visible.map((s) => (
          <g key={s.key} className={s.className}>
            <polyline
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinejoin="round"
              points={series.map((row, i) => `${x(i)},${y(row[s.key] || 0)}`).join(' ')}
            />
            {hover != null && (
              <circle
                cx={x(hover)} cy={y(series[hover][s.key] || 0)} r="2.5"
                fill="currentColor"
              />
            )}
          </g>
        ))}

        {/* transparent hover target over the plot area */}
        <rect
          x={PAD.left} y={PAD.top} width={plotW} height={plotH}
          fill="transparent"
        />
      </svg>

      {hover != null && (
        <div
          className="absolute top-8 pointer-events-none bg-popover border border-border rounded-md shadow-md px-2.5 py-1.5 text-xs z-10"
          style={
            hover / (n - 1 || 1) > 0.6
              ? { right: `${100 - ((x(hover) / VIEW_W) * 100)}%`, marginRight: 8 }
              : { left: `${(x(hover) / VIEW_W) * 100}%`, marginLeft: 8 }
          }
        >
          <div className="font-medium mb-0.5">{fmtDay(series[hover].day, true)}</div>
          {visible.map((s) => (
            <div key={s.key} className="flex items-center gap-1.5">
              <span className={`inline-block w-2 h-0.5 rounded-full bg-current ${s.className}`} />
              <span className="text-muted-foreground">{s.label}</span>
              <span className="ml-auto pl-3 tabular-nums font-medium">
                {(series[hover][s.key] || 0).toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
